import React, { FormEvent, useState } from 'react';
import { Button, Card, Input, Badge } from '@tadagram/ui';
import { useMarketplaceEngine } from '../hooks/useMarketplaceEngine';
import { WithdrawalDraft, WithdrawalStatus } from '../domain/marketplaceTypes';

function createActionKey(): string {
  return `withdraw-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const withdrawalStatusConfig: Record<WithdrawalStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  pending: { label: 'Đang chờ duyệt', variant: 'warning' },
  approved: { label: 'Đã duyệt', variant: 'success' },
  rejected: { label: 'Bị từ chối', variant: 'danger' },
};

const Withdrawals: React.FC = () => {
  const { withdrawals, availableWithdrawCredits, pendingWithdrawCredits, canWithdrawCredits, requestWithdrawal, isSyncing } =
    useMarketplaceEngine();
  const [draft, setDraft] = useState<WithdrawalDraft>({ amount: '', destination: '' });
  const [notice, setNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNotice(null);
    const result = await requestWithdrawal(draft, createActionKey());
    setNotice(result);
    setLoading(false);
    if (result.ok) setDraft({ amount: '', destination: draft.destination });
  };

  return (
    <div className="flex flex-col gap-6">
      {isSyncing && <p className="text-slate-500 text-sm m-0">Đang đồng bộ…</p>}

      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Đã bán (khả dụng)', value: availableWithdrawCredits, colorClass: 'text-slate-900', borderClass: 'border-slate-200' },
          { label: 'Đang chờ rút', value: pendingWithdrawCredits, colorClass: 'text-amber-600', borderClass: 'border-slate-200' },
          { label: 'Có thể rút ngay', value: canWithdrawCredits, colorClass: 'text-emerald-600', borderClass: 'border-emerald-500 shadow-sm' },
        ].map((card) => (
          <Card
            key={card.label}
            className={`p-6 text-center transition-all ${card.borderClass}`}
          >
            <p className="text-sm text-slate-500 font-medium m-0 mb-2">{card.label}</p>
            <p className={`text-3xl font-extrabold m-0 mb-1 ${card.colorClass}`}>
              {card.value.toLocaleString('vi-VN')}
            </p>
            <p className="text-xs text-slate-500 m-0">Tada Credits</p>
          </Card>
        ))}
      </div>

      {/* Form */}
      <Card className="p-7">
        <h3 className="text-lg font-bold text-slate-900 m-0 mb-5">
          Tạo yêu cầu rút tiền
        </h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">
              Số Credits muốn rút *
            </label>
            <Input
              type="number"
              value={draft.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft((p) => ({ ...p, amount: e.target.value }))}
              placeholder={`Tối thiểu 50, tối đa ${canWithdrawCredits.toLocaleString('vi-VN')}`}
              min={50} max={canWithdrawCredits} required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">
              Tài khoản nhận *
            </label>
            <Input
              value={draft.destination}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft((p) => ({ ...p, destination: e.target.value }))}
              placeholder="VD: VIB - ****2833 hoặc Momo 09xx"
              required
            />
          </div>

          {notice && (
            <div
              className={`p-3 rounded-lg text-sm font-medium ${notice.ok ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}
            >
              {notice.message}
            </div>
          )}

          <div className="mt-2">
            <Button type="submit" disabled={loading || canWithdrawCredits < 50}>
              {loading ? 'Đang gửi…' : 'Gửi yêu cầu rút'}
            </Button>
          </div>
        </form>
      </Card>

      {/* History */}
      {withdrawals.length > 0 && (
        <section>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2.5">
            Lịch sử rút tiền
          </h3>
          <div className="flex flex-col gap-2.5">
            {withdrawals.map((w) => {
              const { label, variant } = withdrawalStatusConfig[w.status] ?? withdrawalStatusConfig.pending;
              return (
                <Card
                  key={w.id}
                  className="p-4 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div>
                    <p className="font-bold text-base text-slate-900 m-0 mb-1">
                      {w.amount.toLocaleString('vi-VN')} Credits
                    </p>
                    <p className="text-xs text-slate-500 m-0">
                      {w.destination} · Ref: {w.requestRef} · {w.createdAt}
                    </p>
                  </div>
                  <Badge variant={variant}>
                    {label}
                  </Badge>
                </Card>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default Withdrawals;
